import ItemInfos from "@/src/interfaces/catalog/item-infos"
import { AnswerFromCustomer, ProductTypeId } from "../product/useWizard"
import { CustomSize } from "@/src/context/product/current-product-context"
import ShoppingCart from "@/src/interfaces/system/shopping-cart"
import "@/src/extensions/string"
import "@/src/extensions/number"

export interface FactoryParams {
    quantity: number
    redeemObs: string
    productDetails: ItemInfos.DetailedResponse
    price: number
    hasWizard: boolean
    isCombo: boolean
    totalPrice: number
    answersFromCustomer: AnswerFromCustomer[]
}

interface AnswersDefault {
    subItems: ShoppingCart.CartItem[],
    subItemsOnParent?: ShoppingCart.CartItem[],
    comboPrice: number
}

type ItemType = "observation" | "product" | "productBySizeWithWizard" | "productBySize" | "complement" | "wizardProduct"

export default class ShoppingCartFactory {
    static getMainItemType = (answersFromCustomer: AnswerFromCustomer[], isFromBySize?: boolean): ItemType => {
        if (isFromBySize) {
            if (answersFromCustomer.length > 0)
                return 'productBySizeWithWizard'
            return 'productBySize'
        }
        return 'product'
    }

    static getName = (name: string, redeemObs?: string) => {
        if (redeemObs)
            return `${name} ${redeemObs}`
        return name
    }

    static getPrice = (first?: number, second?: number) => {
        return first ?? second ?? 0
    }

    static getOrderedAnswers(answersFromCustomer: AnswerFromCustomer[], currentProduct?: ItemInfos.DetailedResponse) {
        if (!answersFromCustomer || !currentProduct)
            return []

        return answersFromCustomer.sort((a, b) => {
            const indexA = currentProduct?.itemGroups.findIndex((answer) => answer.id === a.id) ?? 0
            const indexB = currentProduct?.itemGroups.findIndex((answer) => answer.id === b.id) ?? 0
            return indexA - indexB
        })
    }

    static getSizeProduct(params: FactoryParams, sizeProduct: Partial<CustomSize>): ShoppingCart.CartItem {
        const price = sizeProduct.isGroup ? params.price : params.productDetails.salePrice
        const quantity = (sizeProduct.isGroup || params.hasWizard) ? 1 : params.quantity
        const totalPrice = sizeProduct.isGroup ? params.price : (params.hasWizard ? params.totalPrice / params.quantity : params.totalPrice)

        const cartItem: ShoppingCart.CartItem = {
            id: sizeProduct.itemId!,
            externalId: sizeProduct.detailsExternalId!,
            isGroup: sizeProduct.isGroup,
            price: price,
            quantity: quantity,
            name: this.getName(sizeProduct.name!, params.redeemObs),
            personalizedName: `${sizeProduct.name} ${params.redeemObs}`,
            subItems: [],
            totalPrice: totalPrice,
            type: this.getMainItemType(params.answersFromCustomer, true)
        }

        return cartItem
    }

    static getSubItems(params: FactoryParams, sizeProduct: Partial<CustomSize>) {
        const subItems: ShoppingCart.CartItem[] | undefined = sizeProduct.sizesSelected?.map((item) => {
            return {
                id: item.id,
                externalId: item.externalId,
                isGroup: item.isGroup,
                price: item.salePrice,
                quantity: item.quantity,
                name: item.name,
                type: 'productBySize',
                personalizedName: item.name,
                promoId: item.promoId,
                promoPriceType: item.promoPriceType,
                subItems: item.complementsSelecteds ? item.complementsSelecteds.map((complement) => {
                    const flavorsQty = (sizeProduct.sizesSelected && sizeProduct.sizesSelected.length) ?? 1
                    const roundedQty = (1 / flavorsQty).customRound(4)
                    return {
                        id: complement.id,
                        externalId: complement.productExternalId,
                        isGroup: true,
                        price: complement.salePrice,
                        quantity: roundedQty,
                        name: complement.name,
                        type: 'complement',
                        personalizedName: complement.name,
                        totalPrice: complement.salePrice
                    } as ShoppingCart.CartItem
                }) : [],
                totalPrice: sizeProduct.isGroup ? params.price : params.totalPrice
            } as ShoppingCart.CartItem
        })

        return subItems
    }

    static getWizardProduct(params: FactoryParams): ShoppingCart.CartItem {
        const totalPrice = params.hasWizard ? (params.totalPrice / params.quantity) : params.totalPrice
        const quantity = (params.isCombo || params.hasWizard) ? 1 : params.quantity

        const cartItem: ShoppingCart.CartItem = {
            id: params.productDetails.id,
            price: params.productDetails.salePrice,
            quantity: quantity,
            externalId: params.productDetails.productDetailsExternalId,
            name: this.getName(params.productDetails.name, params.redeemObs),
            totalPrice: totalPrice,
            type: 'wizardProduct',
            isGroup: params.isCombo,
            subItems: [] as ShoppingCart.CartItem[]
        }

        return cartItem
    }

    static getSizeAnswersFromCustomer(orderedAnswers: AnswerFromCustomer[]): ShoppingCart.CartItem[] {
        const subItems: ShoppingCart.CartItem[] = []

        orderedAnswers.forEach((item) => {
            item.itemOptionsGroup?.forEach((option) => {
                const child = option.itemChild
                const name = option.answerForMerchant ? option.answerForMerchant : child?.name
                const complementPrice = (option.price && option.price > 0) ? option.price : child?.salePrice

                let priceOption: number = this.getPrice(option.promoPrice, option.price)

                if (item.type === 'complement')
                    priceOption = this.getPrice(option.promoPrice, complementPrice)

                if (item.type === 'product' && priceOption == 0)
                    priceOption = this.getPrice(option.promoPrice, child?.salePrice)

                const currentItem: ShoppingCart.CartItem = {
                    id: option.itemId,
                    price: priceOption,
                    externalId: option.optionExternalId,
                    quantity: option.quantity,
                    name: name,
                    type: (item.type as ItemType) ?? "product",
                    optionId: option.id,
                    groupId: option.itemGroupId
                }

                subItems.push(currentItem)
            })
        })

        return subItems
    }

    static getAnswersFromCustomer(
        orderedAnswers: AnswerFromCustomer[],
        isCombo: boolean,
        linkWizardAnswersToParentItem?: boolean
    ): AnswersDefault {
        let subItems: ShoppingCart.CartItem[] = []
        let subItemsOnParent: ShoppingCart.CartItem[] = []
        let _comboPrice = 0
        let lastProductIndex: number | undefined = undefined

        orderedAnswers.forEach((item) => {
            const isProductItem = item.type === "product"
            const isObservationItem = item.itemOptionsGroup?.some(e => e.answerForCustomer && e.answerForMerchant)
            const isComplement = item.itemOptionsGroup?.some(e => e.itemChild?.itemTypeId === ProductTypeId.Complement)
            const itemType = isObservationItem ? 'observation' : isComplement ? 'complement' : 'product'

            item.itemOptionsGroup?.forEach(option => {
                const child = option.itemChild
                const answerForMerchant = option.answerForMerchant ?? ''
                const itemName = answerForMerchant.isNullOrEmpty() ? child?.name : answerForMerchant
                const complementPrice = (option.price && option.price > 0) ? option.price : child?.salePrice

                let priceOption = this.getPrice(option.promoPrice, option.price)

                if (item.type === 'complement')
                    priceOption = this.getPrice(option.promoPrice, complementPrice)

                if (item.type === 'product' && priceOption === 0)
                    priceOption = this.getPrice(option.promoPrice, child?.salePrice)

                if (isCombo) {
                    priceOption = (priceOption / option.quantity).roundAwayFromZero(2)
                    _comboPrice += (priceOption * option.quantity).roundAwayFromZero(2)
                }

                const currentItem: ShoppingCart.CartItem = {
                    id: option.itemId,
                    optionId: option.id,
                    externalId: option.optionExternalId,
                    price: priceOption,
                    quantity: option.quantity,
                    name: itemName,
                    observation: option.answerForMerchant,
                    type: itemType,
                    subItems: [],
                    groupId: option.itemGroupId,
                }

                if (isProductItem) {
                    subItems.push(currentItem)
                    lastProductIndex = subItems.length - 1
                }
                else if (lastProductIndex !== undefined) {
                    const lastProduct = subItems[lastProductIndex]
                    if (lastProduct) {
                        lastProduct.subItems = lastProduct.subItems || []
                        lastProduct.subItems.push(currentItem)
                    }
                }
                else {
                    subItems.push(currentItem)
                }

                if (lastProductIndex !== undefined) {
                    const lastProduct = subItems[lastProductIndex]
                    const lastItemQty = lastProduct?.quantity ?? 1
                    const isMultipleOf = currentItem.quantity % lastItemQty !== 0 && currentItem.type !== 'observation'

                    if (isMultipleOf) {
                        subItemsOnParent.push(currentItem)
                    }
                }
            })
        })

        if (subItemsOnParent.length > 0) {
            const idsToRemove = new Set(subItemsOnParent.map(i => i.id));
            subItems = this.filterSubItems(subItems, idsToRemove);

            // Se precisa jogar as observações ao pai, remove do item filho em subItems.
            if (linkWizardAnswersToParentItem) {
                subItemsOnParent.map((item) => {
                    const hasObservationOnSubItems = item.subItems && item.subItems.some(i => i.type === 'observation')

                    if (hasObservationOnSubItems && item.subItems) {
                        item.subItems = item.subItems.filter(i => i.type !== 'observation')
                    }
                })
            }
        }

        return {
            subItems: subItems,
            subItemsOnParent: subItemsOnParent,
            comboPrice: _comboPrice,
        }
    }

    private static filterSubItems(items: ShoppingCart.CartItem[], idsToRemove: Set<string>): ShoppingCart.CartItem[] {
        return items
            .filter(item => !idsToRemove.has(item.id))
            .map(item => ({
                ...item,
                subItems: item.subItems && item.subItems.length > 0
                    ? this.filterSubItems(item.subItems, idsToRemove)
                    : []
            }));
    }
}